import { type FC, useMemo, useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, Minus, Plus, ShieldCheck, X } from 'lucide-react'
import type { InternalRole } from '@/hooks/useInternalUsers'
import type { InternalRolePermission } from '@/types/internal'

interface PermissionSettingsModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    role: InternalRole | null
    permissions?: InternalRolePermission[]
    onSave?: (roleId: string, permissions: string[]) => void
    isUpdating?: boolean
}

const PermissionChip = ({
    label,
    mode,
    onClick,
}: {
    label: string
    mode: 'add' | 'remove'
    onClick: () => void
}) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-sm transition hover:shadow-md ${
            mode === 'remove'
                ? 'border-red-100 bg-white text-[#DC1F4E]'
                : 'border-green-100 bg-white text-[#1FC16B]'
        }`}
    >
        {label}
        <span
            className={`flex h-5 w-5 items-center justify-center rounded-full ${
                mode === 'remove'
                    ? 'bg-red-50 text-[#DC1F4E]'
                    : 'bg-green-50 text-[#1FC16B]'
            }`}
        >
            {mode === 'remove' ? (
                <Minus className="h-3 w-3" />
            ) : (
                <Plus className="h-3 w-3" />
            )}
        </span>
    </button>
)

const SectionHeader = ({ label }: { label: string }) => (
    <div className="mb-2 flex items-center gap-3 text-sm font-semibold text-muted-foreground">
        <span>{label}</span>
        <span className="h-px flex-1 rounded-full bg-border" />
    </div>
)

export const PermissionSettingsModal: FC<PermissionSettingsModalProps> = ({
    open,
    onOpenChange,
    role,
    permissions = [],
    onSave,
    isUpdating = false,
}) => {
    const extractPermissionIds = (permissionList?: InternalRolePermission[]) =>
        Array.isArray(permissionList)
            ? permissionList
                  .map(
                      permission =>
                          permission?.id || permission?.key || permission?.name
                  )
                  .filter((id): id is string => Boolean(id))
            : []

    const [assignedPermissionIds, setAssignedPermissionIds] = useState<
        string[]
    >(() => extractPermissionIds(role?.permissions))

    // Map permission IDs to permission objects
    const permissionById = useMemo(() => {
        const map: Record<string, InternalRolePermission> = {}
        permissions.forEach(permission => {
            const permissionId =
                permission.id || permission.key || permission.name
            if (permissionId) {
                map[permissionId] = permission
            }
        })
        return map
    }, [permissions])

    // Map permission keys to permission objects (for backward compatibility)
    const permissionByKey = useMemo(() => {
        const map: Record<string, InternalRolePermission> = {}
        permissions.forEach(permission => {
            map[permission.key] = permission
        })
        return map
    }, [permissions])

    const rolePermissionById = useMemo(() => {
        const map: Record<string, InternalRolePermission> = {}
        role?.permissions?.forEach(permission => {
            const permissionId =
                permission.id || permission.key || permission.name
            if (permissionId) {
                map[permissionId] = permission
            }
            if (permission.key) {
                map[permission.key] = permission
            }
        })
        return map
    }, [role])

    const availablePermissions = useMemo(
        () =>
            permissions.filter(permission => {
                const permissionId =
                    permission.id || permission.key || permission.name
                if (!permissionId) return false
                return !assignedPermissionIds.includes(permissionId)
            }),
        [permissions, assignedPermissionIds]
    )

    if (!role) return null

    const isActive = role.isActive

    const movePermission = (permissionId: string, toAssigned: boolean) => {
        setAssignedPermissionIds(prev => {
            if (toAssigned) {
                if (prev.includes(permissionId)) return prev
                return [...prev, permissionId]
            }

            return prev.filter(item => item !== permissionId)
        })
    }

    const getPermissionLabel = (idOrKey: string) => {
        // Try to find by ID first, then by key for backward compatibility
        return (
            permissionById[idOrKey]?.name ??
            rolePermissionById[idOrKey]?.name ??
            permissionByKey[idOrKey]?.name ??
            idOrKey
        )
    }

    const handleSave = () => {
        if (isUpdating) return
        onSave?.(role.id, assignedPermissionIds)
    }

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen && role) {
            setAssignedPermissionIds(extractPermissionIds(role.permissions))
        } else if (!nextOpen) {
            setAssignedPermissionIds(extractPermissionIds(role?.permissions))
        }
        onOpenChange(nextOpen)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="fixed left-1/2 top-1/2 z-50 h-auto max-h-[90vh] w-full max-w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 bg-transparent p-0 shadow-2xl data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-5xl sm:rounded-3xl"
            >
                <div className="flex h-full flex-col overflow-hidden rounded-3xl bg-white">
                    <div className="flex items-center justify-between border-b px-6 py-4">
                        <span className="rounded-full bg-[#E5F5F9] px-3 py-1 text-xs font-semibold text-[#17A2B8]">
                            PERMISSION SETTINGS
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenChange(false)}
                            className="h-8 w-8 rounded-full"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 pb-6 pt-5">
                        <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl bg-[#F8FAFC] px-5 py-4">
                            <div className="flex items-start gap-3">
                                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm">
                                    <ShieldCheck className="h-5 w-5 text-[#17A2B8]" />
                                </div>
                                <div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <h3 className="text-lg font-semibold text-[#0F172A]">
                                            {role.title}
                                        </h3>
                                        <Badge
                                            variant="secondary"
                                            className={
                                                isActive
                                                    ? 'bg-green-100 text-green-700 hover:bg-green-100'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-100'
                                            }
                                        >
                                            <CheckCircle2 className="h-3 w-3" />
                                            {isActive ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {role.description ||
                                            'Manage all roles & permissions on Ogaboss'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 space-y-6">
                            <div className="space-y-3">
                                <SectionHeader label="Assigned permissions" />
                                <div className="flex flex-wrap gap-3">
                                    {assignedPermissionIds.length === 0 && (
                                        <p className="text-sm text-muted-foreground">
                                            No permissions assigned yet.
                                        </p>
                                    )}
                                    {assignedPermissionIds.map(permissionId => (
                                        <PermissionChip
                                            key={permissionId}
                                            label={getPermissionLabel(
                                                permissionId
                                            )}
                                            mode="remove"
                                            onClick={() =>
                                                movePermission(
                                                    permissionId,
                                                    false
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <SectionHeader label="Available permissions" />
                                <div className="flex flex-wrap gap-3">
                                    {permissions.length === 0 && (
                                        <p className="text-sm text-muted-foreground">
                                            No permissions found. Please try
                                            again later.
                                        </p>
                                    )}
                                    {permissions.length > 0 &&
                                        availablePermissions.length === 0 && (
                                            <p className="text-sm text-muted-foreground">
                                                All permissions assigned.
                                            </p>
                                        )}
                                    {availablePermissions.map(permission => (
                                        <PermissionChip
                                            key={permission.id}
                                            label={permission.name}
                                            mode="add"
                                            onClick={() =>
                                                movePermission(
                                                    permission.id,
                                                    true
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t bg-white px-6 py-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end sm:gap-4">
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                className="h-12 flex-1 rounded-full sm:flex-none sm:min-w-[140px]"
                                disabled={isUpdating}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={isUpdating}
                                className="h-12 flex-1 rounded-full bg-[#17A2B8] text-white hover:bg-[#1493a7] sm:flex-none sm:min-w-[140px]"
                            >
                                {isUpdating ? 'Saving...' : 'Save changes'}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
